import * as React from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Loader2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { errorText } from '@/lib/api';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import { helpText } from '@/help';

export function PageHeader({ title, description, children }: { title: string; description?: string; children?: React.ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

export function Stat({ label, value, hint, className }: { label: string; value: React.ReactNode; hint?: React.ReactNode; className?: string }) {
  return (
    <Card className={className}>
      <CardContent className="p-4">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums" data-testid={`stat-${label}`}>{value}</div>
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

export function Loading() {
  return <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('Loading…')}</div>;
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  return <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">{errorText(error)}</div>;
}

export interface Column<T> {
  key: string;
  label: string;
  render?: (row: T) => React.ReactNode;
  className?: string;
}

export function DataTable<T extends Record<string, any>>({ columns, rows, rowKey, empty, actions, onRowClick }: {
  columns: Column<T>[]; rows: T[] | undefined; rowKey?: (r: T) => string | number; empty?: string;
  actions?: (r: T) => React.ReactNode; onRowClick?: (r: T) => void;
}) {
  return (
    <Table>
      <THead>
        <TR>
          {columns.map((c) => <TH key={c.key} className={c.className}>{c.label}</TH>)}
          {actions && <TH className="text-right">{t('Actions')}</TH>}
        </TR>
      </THead>
      <TBody>
        {(rows ?? []).map((r, i) => (
          <TR key={rowKey ? rowKey(r) : (r.id ?? i)} className={cn(onRowClick && 'cursor-pointer')} onClick={onRowClick ? () => onRowClick(r) : undefined}>
            {columns.map((c) => <TD key={c.key} className={c.className}>{c.render ? c.render(r) : fmtCell(r[c.key])}</TD>)}
            {actions && <TD className="text-right" onClick={(e) => e.stopPropagation()}><div className="flex justify-end gap-1">{actions(r)}</div></TD>}
          </TR>
        ))}
        {rows && rows.length === 0 && (
          <TR><TD colSpan={columns.length + (actions ? 1 : 0)} className="py-8 text-center text-muted-foreground">{empty ?? t('Nothing here yet.')}</TD></TR>
        )}
      </TBody>
    </Table>
  );
}

function fmtCell(v: unknown): React.ReactNode {
  if (v === null || v === undefined) return <span className="text-muted-foreground">-</span>;
  if (typeof v === 'boolean') return v ? t('yes') : t('no');
  if (typeof v === 'object') return <code className="text-xs">{JSON.stringify(v)}</code>;
  const s = String(v);
  return s.length > 60 ? `${s.slice(0, 60)}…` : s;
}

export function Pager({ offset, limit, total, onChange }: { offset: number; limit: number; total?: number; onChange: (o: number) => void }) {
  const end = total === undefined ? offset + limit : Math.min(offset + limit, total);
  return (
    <div className="mt-3 flex items-center justify-end gap-2 text-sm text-muted-foreground">
      <span>{total === undefined ? `${offset + 1}-${end}` : t('{{range}} of {{total}}', { range: total === 0 ? '0' : `${offset + 1}-${end}`, total })}</span>
      <Button variant="outline" size="icon" aria-label={t('Previous page')} disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}><ChevronLeft /></Button>
      <Button variant="outline" size="icon" aria-label={t('Next page')} disabled={total !== undefined && offset + limit >= total} onClick={() => onChange(offset + limit)}><ChevronRight /></Button>
    </div>
  );
}

// a form field: its label, the input, an optional hint under it, and (2026-10-21) an (i) button at the top right
// that shows what the field is for. help = a key of the help catalog (src/help) or the text itself; without one the
// hint is shown. The button sits outside the <label>, so it never becomes the labelled control.
export function Field({ label, children, hint, help }: { label: string; children: React.ReactNode; hint?: string; help?: string }) {
  const info = helpText(help) ?? hint;
  return (
    <div className="relative" data-testid="field" data-help={info ? 'yes' : 'no'}>
      <label className="flex flex-col gap-1.5 text-sm">
        <span className={cn('font-medium', info && 'pr-6')}>{label}</span>
        {children}
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      </label>
      {info && <InfoTip title={label} text={info} className="absolute right-0 top-0" />}
    </div>
  );
}

// the (i) button and its popup: opens on click (or Enter / Space), closes on a second click, Escape, a click outside,
// or scrolling; placed under the button and kept inside the window
export function InfoTip({ title, text, className }: { title: string; text: string; className?: string }) {
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const btn = React.useRef<HTMLButtonElement>(null);
  const pop = React.useRef<HTMLDivElement>(null);
  const id = React.useId();
  React.useEffect(() => {
    if (!pos) return;
    // Escape closes only the popup, not a dialog the field is in: caught on window before the dialog sees it
    const close = (e: Event) => { if (e.type === 'keydown') { if ((e as KeyboardEvent).key !== 'Escape') return; e.stopPropagation(); }
                                  if (e.type === 'mousedown' && btn.current?.contains(e.target as Node)) return;
                                  setPos(null); };
    const closeScroll = (e: Event) => { if (!pop.current?.contains(e.target as Node)) setPos(null); };
    // a click in the popup (portalled outside any dialog) must not count as a click outside that dialog
    const keep = (e: Event) => e.stopPropagation();
    const el = pop.current;
    el?.addEventListener('pointerdown', keep);
    el?.addEventListener('mousedown', keep);
    document.addEventListener('mousedown', close);
    window.addEventListener('keydown', close, true);
    window.addEventListener('scroll', closeScroll, true);
    window.addEventListener('resize', closeScroll);
    return () => { el?.removeEventListener('pointerdown', keep); el?.removeEventListener('mousedown', keep);
                   document.removeEventListener('mousedown', close); window.removeEventListener('keydown', close, true);
                   window.removeEventListener('scroll', closeScroll, true); window.removeEventListener('resize', closeScroll); };
  }, [pos]);
  function toggle(e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    if (pos) { setPos(null); return; }
    const r = btn.current!.getBoundingClientRect();
    const w = Math.min(288, window.innerWidth - 16);
    setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8)) });
  }
  return (
    <>
      <button ref={btn} type="button" onClick={toggle} aria-expanded={!!pos}
              aria-controls={pos ? id : undefined} data-testid="field-help"
              className={cn('inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', className)}>
        <Info className="h-3.5 w-3.5" aria-hidden="true" />
        {/* named by its text, not aria-label, so getByLabel('<field>') still finds only the field */}
        <span className="sr-only">{t('About {{name}}', { name: title.replace(/ \*$/, '') })}</span>
      </button>
      {pos && createPortal(
        <div ref={pop} id={id} role="dialog" aria-label={title.replace(/ \*$/, '')} data-testid="field-help-popup"
             className="pointer-events-auto fixed z-[100] w-72 max-w-[calc(100vw-16px)] rounded-md border bg-popover p-3 text-xs leading-relaxed text-popover-foreground shadow-lg"
             style={{ top: pos.top, left: pos.left }}>
          <div className="mb-1 font-semibold">{title.replace(/ \*$/, '')}</div>
          <div className="whitespace-pre-line">{text}</div>
        </div>, document.body)}
    </>
  );
}
